import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Smartphone, Send, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, CONTACT } from "@/lib/site";

export const Fundraising = () => {
  const [data, setData] = useState({ goal: 5000, raised: 0, donor_count: 0 });
  const [copied, setCopied] = useState("");

  useEffect(() => {
    api
      .get("/fundraiser")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  const pct = Math.min(100, Math.round((data.raised / data.goal) * 100));

  const copy = (value, key) => {
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).catch(() => fallbackCopy(value));
      } else {
        fallbackCopy(value);
      }
    } catch {
      fallbackCopy(value);
    }
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 1800);
  };

  const fallbackCopy = (value) => {
    const el = document.createElement("textarea");
    el.value = value;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand("copy");
    } catch {}
    document.body.removeChild(el);
  };

  return (
    <section
      id="support"
      data-testid="support-section"
      className="relative py-24 sm:py-32 px-6 sm:px-10 bg-[#0A0612] border-y border-[#D4AF37]/15"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Plant A Seed</p>
          <h2 className="font-display text-4xl sm:text-6xl font-semibold text-[#F9F6F0]">
            Help Us <span className="gold-text italic">Open The Doors</span>
          </h2>
          <p className="text-[#A19CA8] text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            Your generous gift helps us secure our space, equip the ministry, and launch in
            Clayton County. Every seed sown brings us closer to the harvest.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="bg-[#130A1F]/70 backdrop-blur-xl border border-[#D4AF37]/25 p-8 sm:p-12"
        >
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[#A19CA8] text-sm uppercase tracking-widest">Raised</p>
              <p className="font-display text-4xl sm:text-5xl gold-text font-bold" data-testid="raised-amount">
                ${data.raised.toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[#A19CA8] text-sm uppercase tracking-widest">Goal</p>
              <p className="font-display text-3xl sm:text-4xl text-[#F9F6F0]">
                ${data.goal.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="h-4 w-full bg-[#06040A] border border-[#D4AF37]/30 overflow-hidden" data-testid="progress-track">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${pct}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-[#B5952F] via-[#D4AF37] to-[#F3D56D] shadow-[0_0_15px_rgba(212,175,55,0.6)]"
            />
          </div>
          <p className="text-[#A19CA8] text-sm mt-3">
            <span className="text-[#D4AF37] font-semibold">{pct}%</span> of our $
            {data.goal.toLocaleString()} goal
          </p>

          <div className="grid sm:grid-cols-2 gap-5 mt-10">
            <a
              href={CONTACT.cashAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="donate-cashapp"
              className="group flex items-center justify-between bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] px-6 py-5 hover:from-[#F3D56D] hover:to-[#D4AF37] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            >
              <span className="flex items-center gap-3">
                <Smartphone className="h-6 w-6" />
                <span className="text-left">
                  <span className="block text-xs uppercase tracking-widest opacity-80">CashApp</span>
                  <span className="block font-bold text-lg">{CONTACT.cashApp}</span>
                </span>
              </span>
            </a>

            <button
              onClick={() => copy(CONTACT.zelle, "zelle")}
              data-testid="donate-zelle"
              className="group flex items-center justify-between bg-[#5D2A8E] text-white px-6 py-5 hover:bg-[#7236AD] transition-all duration-300 border border-[#7236AD]"
            >
              <span className="flex items-center gap-3">
                <Send className="h-6 w-6" />
                <span className="text-left">
                  <span className="block text-xs uppercase tracking-widest opacity-80">Zelle</span>
                  <span className="block font-bold text-lg">{CONTACT.zelle}</span>
                </span>
              </span>
              {copied === "zelle" ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5 opacity-70" />}
            </button>
          </div>
          <p className="text-center text-[#A19CA8]/70 text-xs mt-6 tracking-wide">
            Tap CashApp to give instantly · Tap Zelle to copy our number
          </p>
        </motion.div>
      </div>
    </section>
  );
};
